"use client";

import { ArrowRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthCtaLink } from "./auth-cta-link";
import type { CtaConfig } from "@/config/counties/types";
import { trackFbLead } from "@/lib/analytics/facebook-pixel";

type CTASectionProps = {
  data: CtaConfig;
};

export default function CTASection({ data }: CTASectionProps) {
  const titleLines = data.title.split("\n");
  // Subtitle config may contain <br> line breaks (e.g. asia.json) — render
  // them as real elements instead of piping config through innerHTML.
  const subtitleLines = data.subtitle.split(/<br\s*\/?>/i);

  return (
    <section className="py-20 sm:py-28 relative overflow-hidden bg-primary-surface">
      <div className="absolute inset-0 bg-gradient-to-br from-primary-surface to-primary-surface/80" />

      <div
        className="absolute top-0 left-0 w-[160px] h-[160px] bg-card opacity-20 blur-[64px]"
        style={{ transform: "translate(-20%, -20%)" }}
      />
      <div
        className="absolute bottom-0 right-0 w-[240px] h-[240px] bg-card opacity-20 blur-[64px]"
        style={{ transform: "translate(20%, 20%)" }}
      />

      <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
        <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium mb-8">
          <Zap className="w-4 h-4 fill-white" />
          {data.badgeText}
        </div>

        <h2 className="text-[26px] md:text-[36px] lg:text-[45px] font-bold text-white mb-6 leading-tight max-w-5xl mx-auto">
          {titleLines[0]}
          {titleLines[1] ? <><br className="hidden sm:block" />{titleLines[1]}</> : null}
        </h2>

        <p className="text-white/80 mb-10 text-[14px] md:text-[16px] lg:text-[18px] max-w-2xl mx-auto leading-relaxed">
          {subtitleLines.map((line, idx) => (
            <span key={idx}>
              {idx > 0 && <br />}
              {line}
            </span>
          ))}
        </p>

        <AuthCtaLink href="/dashboard" className="inline-block hover:scale-105 transition-transform duration-200" onClick={() => trackFbLead()}>
          <Button
            size="lg"
            className="group btn-ripple-blue rounded-lg px-8 py-7 bg-card text-primary text-[14px] md:text-[14px] lg:text-[16px] font-medium shadow-xl shadow-primary/20 flex items-center justify-center gap-2 hover:bg-card"
          >
            {data.buttonText}
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
          </Button>
        </AuthCtaLink>
      </div>
    </section>
  );
}
